import { Injectable, Injector, inject } from '@angular/core';
import { Route, Routes } from '@angular/router';

import { flatten } from 'lodash-es';

import { ProductService } from '../products/product.service';
import { standardizeConfig } from './empty_outlet';
import { ROUTE_PROCESSOR, RouteProcessor } from './route-processor';

/**
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class RouteProcessorService {
    private readonly productService = inject(ProductService);
    private readonly rootInjector = inject(Injector);

    processRoutes(routes: Routes[], parentRoute?: Route): Routes {
        return flatten(routes)
            .map(standardizeConfig)
            .map((r: Route) => this.processRoute(r, parentRoute)!)
            .filter((r: Route) => r);
    }

    private processRoute(route: Route, parentRoute?: Route): Route | null {
        const processors = this.getProcessors(route, parentRoute);

        let processedRoute: Route | null = route;
        for (const processor of processors) {
            processedRoute = processor.process(route);
            if (processedRoute == null) {
                return null;
            }
        }

        if (processedRoute.children) {
            processedRoute.children = processedRoute.children.map((c) => this.processRoute(c, parentRoute)!).filter((c) => c);
        }

        return processedRoute;
    }

    private getProcessors(route: Route, parentRoute?: Route): RouteProcessor[] {
        const product: string | undefined = route.data?.['product'] ?? parentRoute?.data?.['product'];

        const currentInjector = this.productService.current.injector;

        if (!product || product === this.productService.current.name) {
            return currentInjector.get<RouteProcessor[]>(ROUTE_PROCESSOR);
        }

        const productMetadata = this.productService.getMetadata(product);

        if (productMetadata.isRegistered) {
            return productMetadata.injector.get<RouteProcessor[]>(ROUTE_PROCESSOR);
        }

        return this.rootInjector.get<RouteProcessor[]>(ROUTE_PROCESSOR);
    }
}
